<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showAddNew="false"
                title="Stock Adjustment Approval"
            />
        </div>

        <div class="px-2">
            <DateQuerySetter
                btnTitle="Go"
                @onClickGo="getStockAdjustments"
            >
                <div class="col-12 col-sm-6 col-md-4 col-lg-2">
                    <label for="colFormLabel" class="col-form-label">Adjustment Number: </label>
                    <input
                        placeholder="Adjustment no"
                        v-model="adjustmentNo"
                        class="form-control"
                    />
                </div>
            </DateQuerySetter>
        </div>

        <div class="d-flex justify-content-end px-2 mt-1">
            <button
                class="btn btn-success"
                :disabled="disableApproveBtn"
                @click="handleApproval"
            >
                Approve Selected
            </button>
        </div>

        <div class="col-12 px-2 mt-1">
            <ApprovalListTable :adjustmentList="adjustmentList" @onView="onViewAdjustment"/>
        </div>

        <div class="px-2">
            <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <AdjustmentDetailModal
            v-if="selectedAdjustmentId"
            :adjustment-id="selectedAdjustmentId"
            :approving="singleApproving"
            @onApprove="handleSingleApproval"
            @onClose="closeDetailModal"
        />

        <GlobalLoader/>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import GlobalLoader from '@/components/atom/GlobalLoader'
import ApprovalListTable from './ApprovalListTable.vue'
import AdjustmentDetailModal from './AdjustmentDetailModal.vue'
import handleStock from '@/services/modules/stock'

const $router = useRouter()
const $route = useRoute()
const $store = useStore()
const showSuccess = inject('showSuccess')
const showError = inject('showError')

const offset = ref(20)
const adjustmentList = ref([])
const adjustmentNo = ref('')
const selectedAdjustmentId = ref(null)
const singleApproving = ref(false)

const { fetchStockAdjustmentList, approveStockAdjustment } = handleStock()

const companyId = computed(() => $route.params.companyId)

const disableApproveBtn = computed(() => {
    return adjustmentList.value.filter(item => item.selected === true).length === 0
})

const getQuery = () => {
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    query += '&start_date=' + $route.query.start
    query += '&end_date=' + $route.query.end
    query += '&status=pending_approval'
    if (adjustmentNo.value) query += '&adjustment_no=' + adjustmentNo.value
    if ($route.query.page) query += '&page=' + $route.query.page
    return query
}

const getStockAdjustments = () => {
    fetchStockAdjustmentList(getQuery())
        .then(res => {
            if (!res.date) {
                adjustmentList.value = []
                $store.commit('resetPagination')
                return
            }
            adjustmentList.value = res.date.data
            setPagination(res.date)
        })
        .catch(err => {
            showError(err.response.message)
        })
}

function setPagination(data) {
    if (!data.data.length) return
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, $route.query)
    routeQuery.page = page
    await $router.push({ path: $route.path, query: routeQuery })
    await getStockAdjustments()
}

const handleApproval = () => {
    const stockAdjustmentIds = adjustmentList.value
        .filter(item => item.selected === true)
        .map(item => item.id)

    if (!stockAdjustmentIds.length) {
        showError('Please select at least one stock adjustment')
        return
    }

    const confirmed = window.confirm('Are you sure you want to approve the selected stock adjustment(s)?')

    if (!confirmed) return

    const data = {
        company_id: companyId.value,
        stock_adjustment_ids: stockAdjustmentIds
    }

    $store.state.loading = true

    approveStockAdjustment(data)
        .then(res => {
            if (!res.status) {
                showError(res.message)
                return
            }
            showSuccess(res.message)
            getStockAdjustments()
        })
        .finally(() => ($store.state.loading = false))
}

const onViewAdjustment = (item) => {
    selectedAdjustmentId.value = item.id
}

const closeDetailModal = () => {
    selectedAdjustmentId.value = null
}

const handleSingleApproval = (adjustmentId) => {
    const confirmed = window.confirm('Are you sure you want to approve this stock adjustment?')

    if (!confirmed) return

    const data = {
        company_id: companyId.value,
        stock_adjustment_ids: [adjustmentId]
    }

    singleApproving.value = true

    approveStockAdjustment(data)
        .then(res => {
            if (!res.status) {
                showError(res.message)
                return
            }
            showSuccess(res.message)
            closeDetailModal()
            getStockAdjustments()
        })
        .finally(() => (singleApproving.value = false))
}

onMounted(() => {
    getStockAdjustments()
})
</script>
